import { useEffect, useRef } from "react";
import { linePairs, outerLoop } from "./constants";

export default function FaceMeshAnimation({
  imageUrl,
  keypoints,
  width,
  height,
}) {
  const canvasRef = useRef();
  const imgRef = useRef();

  useEffect(() => {
    canvasRef.current.width = width;
    canvasRef.current.height = height;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!keypoints || keypoints.length === 0) return;

    // 1. Face center
    const cx = keypoints.reduce((sum, p) => sum + p.x, 0) / keypoints.length;
    const cy = keypoints.reduce((sum, p) => sum + p.y, 0) / keypoints.length;

    // 2. Bounding box and maxR
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;

    keypoints.forEach((p) => {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    });

    const maxR =
      Math.max(
        Math.hypot(cx - minX, cy - minY),
        Math.hypot(cx - minX, cy - maxY),
        Math.hypot(cx - maxX, cy - minY),
        Math.hypot(cx - maxX, cy - maxY),
      ) + 5;

    // Animaton state
    let fadeInStartTime = performance.now();
    let fadeInAlpha = 0;
    let fadeInComplete = false;

    let loopStartTime = null;
    let fading = false;
    let fadeAlpha = 0;
    const minFade = 0.3; // minimum alpha
    const fadeSpeed = 0.005; // decrease per frame
    let fadeOutComplete = true;

    // mesh fade in
    const fadeDuration = 0.5;
    const speed = 0.6;

    function draw() {
      const now = performance.now();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // --- Fade in ---
      if (!fadeInComplete) {
        const elapsed = (now - fadeInStartTime) / 1000;
        fadeInAlpha = Math.min(elapsed / fadeDuration, 1);
        fadeInComplete = fadeInAlpha >= 1;
        if (fadeInComplete) {
          loopStartTime = now;
        }
      }

      // --- Expansion + fade-out ---
      let radius = 0;
      if (fadeInComplete && loopStartTime !== null) {
        if (loopStartTime === now) {
          radius = 0;
        } else {
          const t = (now - loopStartTime) / 1000;
          radius = (t * speed * maxR) % (maxR + 1);
        }

        if (radius >= maxR) {
          radius = maxR;
          if (!fading) {
            fading = true;
            fadeOutComplete = false;
            fadeAlpha = 1;
          }
        }

        if (fading) {
          fadeAlpha -= fadeSpeed;
          if (fadeAlpha < minFade) {
            fadeAlpha = minFade;
            fadeOutComplete = true;
          }
        }

        if (fadeOutComplete) {
          fading = false;
          fadeAlpha = 1;
        }
      }

      ctx.lineWidth = 0.5;
      ctx.strokeStyle = "#ffffff";
      ctx.lineCap = "round";

      for (let [i1, i2] of linePairs) {
        const p1 = keypoints[i1];
        const p2 = keypoints[i2];

        const d1 = Math.hypot(p1.x - cx, p1.y - cy);
        const d2 = Math.hypot(p2.x - cx, p2.y - cy);

        const d = (d1 + d2) * 0.5;

        ctx.globalCompositeOperation = "screen";

        let alpha;
        if (outerLoop.includes(i1) && outerLoop.includes(i2)) {
          alpha = 1;
        } else if (fading) {
          alpha = fadeAlpha;
        } else {
          alpha = d <= radius ? 1 : minFade;
        }

        alpha = Math.max(alpha, minFade);

        if (!fadeInComplete) {
          alpha = alpha * fadeInAlpha;
        }

        ctx.globalAlpha = alpha;

        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      }

      requestAnimationFrame(draw);
    }

    requestAnimationFrame(draw);
  }, [imageUrl, keypoints, width, height]);

  return (
    <div
      style={{ position: "relative", display: "inline-block", height: "100%" }}
    >
      <img
        ref={imgRef}
        src={imageUrl}
        width={100}
        alt="Mesh"
        style={{
          display: "absolute",
          left: 0,
          top: 0,
          objectFit: "cover",
          width: "100%",
          height: "100%",
          transform: "scaleX(-1)",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          placeItems: "center",
        }}
      />
    </div>
  );
}
